import { useEffect, useState } from 'react';
import EmbedPopupIcon from '../icons/EmbedPopupIcon.jsx';
import EmbedInlineIcon from '../icons/EmbedInlineIcon.jsx';
import IconSelectButton from '../IconSelectButton.jsx';
import { useCompleteStep } from '../../lib/Onboarding.js';
import { useFormSharingQuery } from '../../lib/Queries';
import ShareOptions from './ShareOptions.jsx';
import Loading from '~/components/Loading';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import { isTestMode, TEST_MODE_PARAM } from '~/lib/TestModeHelpers.js';

const CopyButton = ({ children, copyText, className }) => {
    const [hasCopied, setHasCopied] = useState(false);
    function onClick(ev) {
        navigator.clipboard.writeText(copyText);
        setHasCopied(true);
    }

    useEffect(() => {
        setHasCopied(false);
    }, [copyText]);

    return (
        <button
            type="button"
            className={`btn-pill ${className}`}
            onClick={onClick}
        >
            {!hasCopied && children}
            {hasCopied && 'Copied!'}
        </button>
    );
};

export const ShareLink = ({ url, hideLabel }) => {
    return (
        <div className="mb-6">
            {!hideLabel && (<div className="input-label">Share Link</div>)}
            <input
                type="text"
                className="w-full mb-2"
                readOnly={true}
                value={url}
            />
            <div className="flex ">
                <CopyButton copyText={url} className="mr-2">
                    Copy Link
                </CopyButton>
                <a href={url} target="_blank" className="btn-light-pill">
                    Open in new tab
                </a>
            </div>
        </div>
    );
};

export const ShareEmbed = ({ embedPrefix, url, hideLabel }) => {
    const [embedType, setEmbedType] = useState('inline');
    const parsedUrl = new URL(url);
    const urlSuffix = encodeURIComponent(parsedUrl.pathname + parsedUrl.search);
    let embedCode = `<script type="text/javascript" src="${embedPrefix}${urlSuffix}"></script>\n\n`;
    if (embedType === 'popup') {
        embedCode += `<a href="#" class="breely-popup-trigger" data-url="${url}">Book Now</a> <!-- Any clicks on elements with the class breely-popup-trigger will trigger the modal -->`;
    } else {
        embedCode += `<div class="breely-inline" data-url="${url}"></div> <!-- Add this where you want it embedded -->`;
    }
    return (
        <div>
            {!hideLabel && (<div className="input-label">Embed in Webpage</div>)}
            <textarea
                className="max-w-2xl mb-2 block w-full h-32"
                value={embedCode}
                readOnly={true}
            />
            <div className="flex items-center mb-2">
                <IconSelectButton
                    label="Inline"
                    onClick={() => setEmbedType('inline')}
                    isSelected={embedType === 'inline'}
                    icon={
                        <EmbedInlineIcon isSelected={embedType === 'inline'} />
                    }
                    className="mr-2"
                />
                <IconSelectButton
                    label="Popup"
                    isSelected={embedType === 'popup'}
                    icon={<EmbedPopupIcon isSelected={embedType === 'popup'} />}
                    onClick={() => setEmbedType('popup')}
                />
            </div>
            <CopyButton copyText={embedCode} key={embedType}>
                Copy Code
            </CopyButton>
        </div>
    );
};

const Share = ({ form, url, embedPrefix, hasClientIdentifier }) => {
    const { complete } = useCompleteStep();
    useEffect(() => {
        complete('scheduling-share');
    }, []);

    const [shareUrl, setShareUrl] = useState(url);

    useEffect(() => {
        if (url) setShareUrl(url);
    }, [url]);

    if (!shareUrl) {
        return <Loading />;
    }

    // Check if the page has `test` in the query string
    const isTest = isTestMode();
    const testShareUrl = new URL(shareUrl);
    testShareUrl.searchParams.set(TEST_MODE_PARAM, 'true');

    return (
        <div className="mt-6 max-w-2xl">
            {!hasClientIdentifier && (
                <div className="text-red-600">
                    Please add a question labeled "Email". This will be used to
                    identify clients.
                    <div className="text-gray-500">
                        Note, you can still send this form directly to clients
                        if you include their ID
                    </div>
                </div>
            )}
            {hasClientIdentifier && (
                <div>
                    {isTest && (
                        <InfoMessageBlock showIcon={true} className="mb-6">
                            Try making a test booking to preview the clients' scheduling experience:
                            {/*<div className="mt-2">*/}
                            {/*    <a href={testShareUrl.toString()} target="_blank">*/}
                            {/*        {testShareUrl.toString()}*/}
                            {/*    </a>*/}
                            {/*</div>*/}
                            <button
                                type="button"
                                className="btn-pill mt-2"
                                onClick={() => {
                                    window.open(testShareUrl.toString(), '_blank');
                                }}
                            >
                                Test Booking
                            </button>
                        </InfoMessageBlock>
                    )}
                    <ShareOptions
                        form={form}
                        setShareUrl={value => {
                            setShareUrl(value);
                        }}
                        shareUrl={shareUrl}
                    />

                    <ShareLink url={shareUrl} />
                    <ShareEmbed url={shareUrl} embedPrefix={embedPrefix} />
                </div>
            )}
        </div>
    );
};

export default Share;
